import { FLOATING_BADGE_CLASS } from "components/PoweredByPill";
import { Button } from "primereact/button";
import styled, { createGlobalStyle } from "styled-components";
import { MODULES } from "training/curriculum/modules";
import {
  advanceKind,
  CARD_POPOVER_CLASS,
  DONE_HINT,
  POPOVER_CLASS,
  TOUR_PANELS_CLASS,
} from "training/engine/steps";
import { ITourStep, TourModule } from "training/types";

/**
 * CLE-3015 — the React content portalled into the driver.js popover: where the trainee is ("Module 2 ·
 * step 7 of 16" and the module's title; the module's purpose is on its title screen, ModuleTitleScreen,
 * shown before its first step), the on-screen label the step is about, the curriculum text, an optional picture, and
 * the buttons. driver.js's own title and buttons are not used (every step passes `showButtons: []`), so
 * this component is the whole popover body.
 *
 * - Next appears on every step that moves on with Next (look, fill, card, picture); a `click` step moves
 *   on when the trainee clicks the highlighted element, an `event` step when the page reports it. On a
 *   gated fill step (`done`) Next is disabled while `canAdvance` is false, with one line saying why.
 * - Back re-shows the previous step on this page (e.g. to fix a field Create and Send refused).
 * - Exit saves where the trainee is and closes the tour; the banner's Resume brings them back.
 *
 * `data-step-id` on the body is a test hook only (CLE-3015 PR 5): the nightly walk reads the step id from it.
 */
/** The overlay panels PrimeReact portals to <body> (`appendTo` default) that a fill step's field opens. */
const BODY_PANELS = [
  ".p-dropdown-panel",
  ".p-autocomplete-panel",
  ".p-multiselect-panel",
  ".p-datepicker",
].map((panel) => `body.${TOUR_PANELS_CLASS} > ${panel}`);

/**
 * Fill steps (TOUR_PANELS_CLASS, dev QA of #1438): the lists PrimeReact portals to <body> — a Dropdown's
 * options, an AutoComplete's suggestions, a Calendar pop-up — open from the highlighted field but render
 * outside it, under driver.js's overlay (z-index 10000) and its `.driver-active * { pointer-events: none }`,
 * so a mouse could not pick from them. Lift them above the overlay and the popover (driver's 1000000000;
 * PrimeReact sets z-index inline, hence !important) and let them take clicks. Exported for the test, which
 * checks the computed style against driver.css.
 */
export const TOUR_PANELS_CSS = `
  ${BODY_PANELS.join(",\n  ")} {
    z-index: 1000000001 !important;
  }

  ${BODY_PANELS.flatMap((panel) => [panel, `${panel} *`]).join(",\n  ")} {
    pointer-events: auto;
  }
`;

/**
 * The floating "Powered by Clear Health" badge (PoweredByPill, fixed to the viewport's bottom right) steps
 * aside while a tour runs (`driver-active` is driver.js's class on <body>). It is fixed above the page, so it
 * covered the highlighted button under it: John's review of CLE-3015, Module 2's Create and Send. While the
 * tour runs nothing outside the highlight can be clicked anyway. Exported for the test.
 */
export const TOUR_HIDDEN_BADGE_CSS = `
  body.driver-active .${FLOATING_BADGE_CLASS} {
    visibility: hidden;
  }
`;

export const TourPopoverGlobalStyle = createGlobalStyle`
  .driver-popover.${POPOVER_CLASS} {
    max-width: 380px;
    min-width: 280px;
    padding: 16px;
    border: 1px solid var(--brand-gray-200);
    border-radius: 12px;
    font-family: var(--font-family);
    color: var(--brand-black);
  }

  /* A card step (no element): centred over the page, so it can be wider and wrap less. */
  .driver-popover.${POPOVER_CLASS}.${CARD_POPOVER_CLASS} {
    max-width: min(560px, calc(100vw - 32px));
  }

  .driver-popover.${POPOVER_CLASS} .driver-popover-title,
  .driver-popover.${POPOVER_CLASS} .driver-popover-footer,
  .driver-popover.${POPOVER_CLASS} .driver-popover-close-btn {
    display: none !important;
  }

  .driver-popover.${POPOVER_CLASS} .driver-popover-description {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.45;
  }

  ${TOUR_PANELS_CSS}

  ${TOUR_HIDDEN_BADGE_CSS}
`;

const Header = styled.div`
  margin-bottom: 8px;
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--brand-purple-700);
  font-size: 0.75rem;
  font-weight: var(--font-weight-semibold);
`;

const ModuleTitle = styled.div`
  margin-top: 2px;
  color: var(--brand-gray-600);
  font-size: 0.75rem;
`;

const StepLabel = styled.div`
  margin-bottom: 6px;
  font-size: 0.95rem;
  font-weight: var(--font-weight-semibold);
`;

const Paragraph = styled.p`
  margin: 0 0 8px;
`;

/** Consecutive "Label: explanation" lines (the statuses on 1.5), one per line with little spacing. */
const TermList = styled.ul`
  margin: 0 0 8px;
  padding: 0;
  list-style: none;

  li + li {
    margin-top: 2px;
  }
`;

/** "Action Needed: a draft, ..." -> label and explanation. A short label, no sentence end inside it. */
const TERM_LINE = /^([^:.]{1,30}): (.+)$/;

type TextBlock =
  | { kind: "paragraph"; text: string }
  | { kind: "terms"; lines: Array<{ term: string; rest: string }> };

/** Paragraphs from the step text, with runs of "Label: explanation" lines gathered into one list. */
export function textBlocks(text: string): TextBlock[] {
  const blocks: TextBlock[] = [];
  text
    .split("\n")
    .filter(Boolean)
    .forEach((line) => {
      const match = TERM_LINE.exec(line);
      if (!match) {
        blocks.push({ kind: "paragraph", text: line });
        return;
      }
      const last = blocks[blocks.length - 1];
      const entry = { term: match[1], rest: match[2] };
      if (last?.kind === "terms") last.lines.push(entry);
      else blocks.push({ kind: "terms", lines: [entry] });
    });
  return blocks;
}

const Hint = styled.p`
  margin: 4px 0 0;
  color: var(--brand-gray-600);
  font-size: 0.8rem;
  font-style: italic;
`;

const Picture = styled.img`
  display: block;
  width: 100%;
  margin: 8px 0;
  border: 1px solid var(--brand-gray-200);
  border-radius: 8px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 12px;
`;

export interface ITourPosition {
  module: TourModule;
  index: number;
  total: number;
}

interface ITourPopoverProps {
  step: ITourStep;
  position: ITourPosition | null;
  canGoBack: boolean;
  /** False while the step's gate (`done`) is shut: Next is disabled and the hint line shows. */
  canAdvance?: boolean;
  onNext: () => void;
  onBack: () => void;
  onExit: () => void;
}

export function TourPopover({
  step,
  position,
  canGoBack,
  canAdvance = true,
  onNext,
  onBack,
  onExit,
}: ITourPopoverProps) {
  const kind = advanceKind(step);
  const moduleInfo = position ? MODULES[position.module] : undefined;

  return (
    <div
      data-testid="tour-popover"
      // The nightly Cypress walk (cypress/e2e/clear-certified-tour.cy.ts) reads which step is showing from here.
      data-step-id={step.id}
      role="dialog"
      aria-label="Clear Certified"
    >
      <Header>
        <Eyebrow>
          <i className="pi pi-verified" aria-hidden />
          <span>
            {position
              ? `Module ${position.module} · step ${position.index} of ${position.total}`
              : "Clear Certified"}
          </span>
        </Eyebrow>
        {moduleInfo && (
          <ModuleTitle data-testid="tour-module-title">
            {moduleInfo.title}
          </ModuleTitle>
        )}
      </Header>

      {(step.title ?? step.label) && (
        <StepLabel>{step.title ?? step.label}</StepLabel>
      )}

      {textBlocks(step.text).map((block) =>
        block.kind === "paragraph" ? (
          <Paragraph key={block.text}>{block.text}</Paragraph>
        ) : (
          <TermList key={block.lines[0].term}>
            {block.lines.map(({ term, rest }) => (
              <li key={term}>
                <strong>{term}:</strong> {rest}
              </li>
            ))}
          </TermList>
        )
      )}

      {step.picture && <Picture src={step.picture} alt="" />}

      {kind === "click" && (
        <Hint>Click the highlighted button to continue.</Hint>
      )}
      {kind === "event" && (
        <Hint>The tour continues once your estimate is created.</Hint>
      )}
      {kind === "next" && !canAdvance && (
        <Hint data-testid="tour-done-hint">{step.doneHint ?? DONE_HINT}</Hint>
      )}

      <Footer>
        <Button
          type="button"
          label="Exit"
          className="p-button-text p-button-plain p-button-sm"
          onClick={onExit}
        />
        <div className="flex gap-2">
          {canGoBack && (
            <Button
              type="button"
              label="Back"
              className="p-button-outlined p-button-sm"
              onClick={onBack}
            />
          )}
          {kind === "next" && (
            <Button
              type="button"
              label={step.continueLabel ?? "Next"}
              className="p-button-sm"
              disabled={!canAdvance}
              onClick={onNext}
            />
          )}
        </div>
      </Footer>
    </div>
  );
}
